import type { InstallationResolveLineItem } from "../types/installation";
import { readInstallationLineTaxable } from "../utils/readResolveTaxable";
import {
  getOriginalCatalogUnitPrice,
  getResolveRequestUnitPrice,
  orderLineKey,
} from "utils/orderResolveLinePricing";
import { round2 } from "utils/fixedAmountDiscountAllocation";

export type InstallationAddResolveCatalogProduct = {
  product_id: string;
  variant_id?: string;
  name?: string;
  sku?: string;
  quantity?: number;
  inventory_type?: string;
  is_new_catalog?: boolean;
  is_taxable?: boolean;
  unit_price?: number;
  retail_price?: number;
  unit_retail_price?: number;
};

export type InstallationAddResolveCustomProduct = {
  product_id: string;
  variant_id?: string;
  item_name?: string;
  quantity?: number;
  Price?: number;
  is_taxable?: boolean;
};

export type BuildInstallationAddResolveLineItemsInput = {
  catalogProducts: InstallationAddResolveCatalogProduct[];
  customProducts?: InstallationAddResolveCustomProduct[];
  manualUnitPriceMap: Record<string, number>;
  defaultInventorySource: string;
};

/**
 * Build resolve line_items for installation add flows (catalog + optional custom products).
 * Includes sku so TaxCloud itemId stays within the 50-character limit.
 */
export function buildInstallationAddResolveLineItems(
  input: BuildInstallationAddResolveLineItemsInput
): InstallationResolveLineItem[] {
  const rows: InstallationResolveLineItem[] = [];

  for (const p of input.catalogProducts || []) {
    if (!p.is_new_catalog || !p.product_id) continue;

    const key = orderLineKey(p.product_id, p.variant_id);
    const original = getOriginalCatalogUnitPrice(p);
    const unitPrice = getResolveRequestUnitPrice(key, original, input.manualUnitPriceMap);
    const isManual = input.manualUnitPriceMap[key] !== undefined;
    const sku = String(p.sku ?? "").trim();

    rows.push({
      product_id: String(p.product_id),
      variant_id: String(p.variant_id ?? ""),
      product_name: String(p.name ?? ""),
      ...(sku ? { sku } : {}),
      inventory_source: p.inventory_type || input.defaultInventorySource,
      is_new_purchase: true,
      quantity: Math.max(1, Number(p.quantity) || 1),
      unit_price: unitPrice,
      ...(isManual ? { unit_retail_price: unitPrice, changed_unit_price: unitPrice } : {}),
      is_price_overridden: isManual,
      is_taxable: readInstallationLineTaxable(p as Record<string, unknown>, true),
      itemType: "default",
    });
  }

  for (const cp of input.customProducts || []) {
    if (!cp.product_id) continue;

    rows.push({
      product_id: String(cp.product_id),
      variant_id: String(cp.variant_id ?? ""),
      product_name: String(cp.item_name || "Custom Item"),
      sku: `custom_sku_${cp.product_id}`,
      inventory_source: "custom",
      is_new_purchase: true,
      quantity: Math.max(1, Number(cp.quantity) || 1),
      unit_price: round2(Number(cp.Price) || 0),
      is_price_overridden: true,
      is_taxable: readInstallationLineTaxable(cp as Record<string, unknown>, false),
      itemType: "default",
    });
  }

  return rows;
}
